/**
 * TenantFormDialog (MUI)
 *
 * 테넌트 생성/편집 폼. teams-tab의 TenantAdminSection.tsx의 MUI 포팅.
 * - editingTenantId 가 주어지면 편집 모드 (ID 수정 불가)
 * - 저장 시 /api/admin/tenants API 호출
 */

import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import * as tenantsApi from "../api/tenants";
import type { Agent } from "../api/types";

interface TenantFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editingAgent: Agent | null;
}

interface FormState {
  id: string;
  name: string;
  nameKorean: string;
  emoji: string;
  bucket: string;
  enabled: boolean;
}

const EMPTY_FORM: FormState = {
  id: "",
  name: "",
  nameKorean: "",
  emoji: "",
  bucket: "",
  enabled: true,
};

export function TenantFormDialog({
  open,
  onClose,
  onSaved,
  editingAgent,
}: TenantFormDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!editingAgent;

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (!editingAgent) {
      setForm(EMPTY_FORM);
      return;
    }
    // 편집 모드 — seed with Agent 데이터, 그 후 전체 config 로드
    setForm({
      id: editingAgent.tenantCode,
      name: editingAgent.name,
      nameKorean: editingAgent.nameKorean,
      emoji: editingAgent.emoji,
      bucket: "",
      enabled: true,
    });
    setLoading(true);
    tenantsApi
      .fetchTenantConfigs()
      .then((configs) => {
        const t = configs.find((c) => c.id === editingAgent.tenantCode);
        if (t) {
          setForm({
            id: t.id,
            name: t.name,
            nameKorean: t.nameKorean,
            emoji: t.identity.emoji,
            bucket: t.bucket,
            enabled: t.enabled,
          });
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : "config 로드 실패"))
      .finally(() => setLoading(false));
  }, [open, editingAgent]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const identity = {
        name: `${form.nameKorean} AI`,
        emoji: form.emoji,
      };
      if (isEdit) {
        await tenantsApi.updateTenant(form.id, {
          name: form.name,
          nameKorean: form.nameKorean,
          bucket: form.bucket,
          identity,
          enabled: form.enabled,
        });
      } else {
        const bucket = form.bucket || `hyperlounge-${form.id}0-foldersync`;
        await tenantsApi.createTenant({
          id: form.id,
          name: form.name,
          nameKorean: form.nameKorean,
          bucket,
          identity,
          enabled: form.enabled,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "저장 실패");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingAgent) return;
    if (!window.confirm(`${editingAgent.nameKorean} 테넌트를 삭제하시겠습니까?`)) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await tenantsApi.deleteTenant(editingAgent.tenantCode);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "삭제 실패");
    } finally {
      setSaving(false);
    }
  };

  const canSave =
    !saving &&
    form.id.length > 0 &&
    form.name.length > 0 &&
    form.nameKorean.length > 0 &&
    form.emoji.length > 0;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? "테넌트 편집" : "테넌트 추가"}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="테넌트 ID (7자 hex)"
              size="small"
              value={form.id}
              onChange={(e) =>
                setForm({ ...form, id: e.target.value.toLowerCase() })
              }
              placeholder="예: a1b2c3d"
              inputProps={{ maxLength: 7 }}
              disabled={isEdit}
              fullWidth
              helperText={isEdit ? "ID는 수정할 수 없습니다" : undefined}
            />
            <TextField
              label="영문 이름"
              size="small"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="예: Donggu Bio"
              fullWidth
            />
            <TextField
              label="한글 이름"
              size="small"
              value={form.nameKorean}
              onChange={(e) => setForm({ ...form, nameKorean: e.target.value })}
              placeholder="예: 동구바이오제약"
              fullWidth
            />
            <TextField
              label="이모지"
              size="small"
              value={form.emoji}
              onChange={(e) => setForm({ ...form, emoji: e.target.value })}
              placeholder="예: 💊"
              sx={{ width: 120 }}
            />
            <TextField
              label="GCS 버킷"
              size="small"
              value={form.bucket}
              onChange={(e) => setForm({ ...form, bucket: e.target.value })}
              placeholder={
                form.id
                  ? `hyperlounge-${form.id}0-foldersync`
                  : "hyperlounge-{id}0-foldersync"
              }
              fullWidth
              helperText="비워두면 기본 패턴으로 자동 생성"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.enabled}
                  onChange={(_, v) => setForm({ ...form, enabled: v })}
                />
              }
              label="활성화"
            />
            <Typography variant="caption" color="text.secondary">
              아이덴티티 이름: {form.nameKorean || "(한글 이름)"} AI
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        {isEdit ? (
          <Button color="error" onClick={handleDelete} disabled={saving}>
            삭제
          </Button>
        ) : (
          <span />
        )}
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={saving}>
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {saving ? "저장 중..." : isEdit ? "저장" : "생성"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
